import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { MapPin, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/**
 * Persetujuan lokasi nakes (Okt 2026). Sekali setuju di aplikasi, tidak ditanya lagi
 * (kecuali versi teks persetujuan berubah). Izin di tingkat HP tetap diatur oleh HP.
 *
 * Teks harus tetap jujur: lokasi diambil SEKALI saat OTW, tidak dilacak setelahnya.
 * [DIMATIKAN 31 Okt 2026, lihat LOCATION_REMINDER_ENABLED] Kalau izin HP belum aktif: banner kuning tetap menempel dan kartu pengingat kecil muncul di bawah
 * layar tiap aplikasi dibuka dan berkala. Kartu bisa ditutup dengan satu ketukan, tanpa syarat apa pun.
 * Tidak ada penguncian apply job (dibatalkan 31 Okt 2026).
 * Status izin dibaca langsung dari HP nakes, jadi begitu izin aktif pengingat berhenti sendiri.
 */
// Saklar pengingat (banner kuning + kartu bawah layar). DIMATIKAN 31 Okt 2026: di sebagian iPhone status
// izin selalu terbaca "belum diizinkan" walau lokasi bisa dipakai, jadi pengingat salah kira dan mengganggu.
// Pelaporan status izin ke Direktori Nakes dan dialog persetujuan tetap jalan. Ubah ke true untuk menyalakan lagi.
const LOCATION_REMINDER_ENABLED = false;
const NAG_EVERY_MS = 15 * 60 * 1000; // jeda antar kartu pengingat
const NAG_AWAY_RESET_MS = 2 * 60 * 1000; // ditinggal lebih dari ini = dianggap buka aplikasi lagi
const NAG_MIN_GAP_MS = 5 * 60 * 1000; // kartu tidak muncul lagi dalam jeda ini walau aplikasi ditinggal sebentar

function LocationGuide() {
  return (
    <>
      <div>1. Nyalakan lokasi di HP. iPhone: Pengaturan, Privasi dan Keamanan, Layanan Lokasi, nyalakan. Android: geser layar dari atas, tekan ikon Lokasi sampai menyala.</div>
      <div>2. Izinkan Vaxa memakai lokasi. iPhone: Pengaturan, Privasi dan Keamanan, Layanan Lokasi, Situs Web Safari, pilih "Saat Menggunakan App". Android: Pengaturan, Aplikasi, Chrome, Izin, Lokasi, pilih "Izinkan hanya saat aplikasi digunakan" dan nyalakan "Gunakan lokasi akurat". Kalau Vaxa dipasang di layar utama HP, lakukan hal yang sama di menu aplikasi Vaxa.</div>
      <div>3. Tutup aplikasi Vaxa sampai benar-benar mati, lalu buka lagi. Kalau muncul pertanyaan izin lokasi, pilih "Izinkan".</div>
      <div>Menu bisa berbeda di tiap merek HP. Kalau bingung, hubungi admin dan sebutkan merek dan tipe HP kamu.</div>
    </>
  );
}

// Kartu pengingat kecil di bawah layar: satu ketukan untuk menutup, muncul lagi nanti.
function LocationNagCard({ denied, onRecheck, onClose }) {
  const [busy, setBusy] = useState(false);
  return (
    <div
      role="alert"
      className="fixed inset-x-3 bottom-20 z-[60] rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900 shadow-lg sm:left-auto sm:right-4 sm:max-w-sm"
      data-testid="location-nag-card"
    >
      <div className="flex items-start gap-2">
        <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="flex-1 text-sm">
          <div className="font-medium">Lokasi HP belum aktif</div>
          <div className="mt-0.5 text-xs">Aktifkan izin lokasi supaya estimasi tiba saat OTW bisa dihitung.</div>
          {!denied && (
            <button
              type="button"
              disabled={busy}
              onClick={async () => { setBusy(true); await onRecheck(); setBusy(false); }}
              className="mt-2 rounded bg-amber-600 px-2 py-1 text-xs text-white disabled:opacity-60"
              data-testid="location-nag-allow"
            >
              Izinkan lokasi
            </button>
          )}
        </div>
        <button type="button" onClick={onClose} aria-label="Tutup" className="p-1" data-testid="location-nag-close">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function NakesLocationConsentPopup() {
  const { user } = useAuth();
  const [needsPrompt, setNeedsPrompt] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false); // cek persetujuan selesai (berhasil atau gagal)
  // Status izin lokasi HP: 'granted' | 'denied' | 'prompt' | null (belum diketahui / browser tidak bisa menjawab)
  const [permState, setPermState] = useState(null);
  const denied = permState === 'denied';
  const [showGuide, setShowGuide] = useState(false);
  const [nagOpen, setNagOpen] = useState(false);
  const lastNagRef = useRef(0); // 0 = belum pernah tampil sejak aplikasi dibuka
  const hiddenAtRef = useRef(0);
  const { pathname } = useLocation();
  // Permintaan dari tombol OTW (nakes belum setuju): { onAgree, onDecline }
  const [requested, setRequested] = useState(null);
  const requestedRef = useRef(null);
  requestedRef.current = requested;

  // Lapor status izin lokasi HP ke server (hanya dicatat, supaya admin tahu siapa yang belum aktif).
  // Dikirim kalau status berubah atau sudah lebih dari 12 jam sejak laporan terakhir.
  const reportPermission = useCallback(async (state) => {
    const uid = user?.user_id;
    if (!uid) return;
    try {
      let last = null;
      try { last = JSON.parse(localStorage.getItem('vm_loc_perm_reported') || 'null'); } catch (_e) { /* abaikan */ }
      const fresh = last && last.uid === uid && last.state === state && Date.now() - last.t < 12 * 3600 * 1000;
      if (fresh) return;
      await axios.post(`${API}/nakes/location-permission`, { state }, { headers: authHeaders(), timeout: 15000 });
      try { localStorage.setItem('vm_loc_perm_reported', JSON.stringify({ uid, state, t: Date.now() })); } catch (_e) { /* abaikan */ }
    } catch (_e) { /* gagal lapor (sinyal): coba lagi saat aplikasi dibuka berikutnya */ }
  }, [user?.user_id]);

  const checkPermission = useCallback(async () => {
    try {
      if (!navigator.permissions?.query) { reportPermission('unknown'); return; }
      const st = await navigator.permissions.query({ name: 'geolocation' });
      setPermState(st.state);
      reportPermission(st.state);
      st.onchange = () => { setPermState(st.state); reportPermission(st.state); };
    } catch (_e) { reportPermission('unknown'); /* browser tidak mendukung */ }
  }, [reportPermission]);

  // Cek izin HP sendiri, tidak menunggu cek persetujuan di bawah (yang bisa gagal karena sinyal).
  useEffect(() => {
    if (!user?.user_id) return;
    checkPermission();
  }, [user?.user_id, checkPermission]);

  // Cek ulang izin: baca status dari HP, kalau belum granted coba baca lokasi sungguhan
  // (memunculkan pertanyaan izin bila statusnya "prompt", dan menangkap status yang salah baca).
  const recheckPermission = useCallback(async () => {
    try {
      if (navigator.permissions?.query) {
        const st = await navigator.permissions.query({ name: 'geolocation' });
        setPermState(st.state);
        reportPermission(st.state);
        if (st.state === 'granted') return 'granted';
      }
    } catch (_e) { /* lanjut ke tes lokasi */ }
    return new Promise((resolve) => {
      try {
        if (!navigator.geolocation) { resolve('unknown'); return; }
        navigator.geolocation.getCurrentPosition(
          () => { setPermState('granted'); reportPermission('granted'); resolve('granted'); },
          (err) => {
            if (err?.code === 1) { setPermState('denied'); reportPermission('denied'); resolve('denied'); } else { resolve('error'); }
          },
          { timeout: 10000, maximumAge: 60000 },
        );
      } catch (_e) { resolve('unknown'); }
    });
  }, [reportPermission]);

  // Nakes kembali ke aplikasi (mis. setelah mengubah pengaturan HP): baca ulang status izin.
  // Ditinggal lebih dari 2 menit dianggap membuka aplikasi lagi, pengingat boleh muncul.
  useEffect(() => {
    if (!user?.user_id) return undefined;
    const onVisibility = async () => {
      if (document.visibilityState === 'hidden') { hiddenAtRef.current = Date.now(); return; }
      if (hiddenAtRef.current && Date.now() - hiddenAtRef.current > NAG_AWAY_RESET_MS
          && Date.now() - lastNagRef.current > NAG_MIN_GAP_MS) lastNagRef.current = 0;
      hiddenAtRef.current = 0;
      try {
        if (!navigator.permissions?.query) return;
        const st = await navigator.permissions.query({ name: 'geolocation' });
        setPermState(st.state);
        reportPermission(st.state);
      } catch (_e) { /* abaikan */ }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [user?.user_id, reportPermission]);

  useEffect(() => {
    if (!user?.user_id) return;
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/nakes/location-consent`, { headers: authHeaders(), timeout: 15000 });
        if (!alive) return;
        if (r.data?.needs_prompt) { setNeedsPrompt(true); setOpen(true); }
      } catch (_e) { /* gagal cek: jangan ganggu nakes */ }
      if (alive) setConsentChecked(true);
    })();
    return () => { alive = false; };
  }, [user?.user_id]);

  // Tombol OTW meminta persetujuan lagi (Okt 2026). Penerima menandai handled supaya
  // pengirim tahu jendela ini yang menangani; kalau tidak ada penerima, pengirim jalan sendiri.
  useEffect(() => {
    const onRequest = (e) => {
      const d = e?.detail;
      if (!d || typeof d.onAgree !== 'function' || typeof d.onDecline !== 'function') return;
      d.handled = true;
      // Permintaan lama yang belum dijawab dicatat belum setuju, supaya tidak hilang tanpa catatan.
      const prev = requestedRef.current;
      if (prev) { try { prev.onDecline(); } catch (_e) { /* abaikan */ } }
      requestedRef.current = { onAgree: d.onAgree, onDecline: d.onDecline };
      setRequested({ onAgree: d.onAgree, onDecline: d.onDecline });
      setOpen(true);
    };
    window.addEventListener('vm:location-consent-request', onRequest);
    return () => {
      window.removeEventListener('vm:location-consent-request', onRequest);
      // Halaman ditinggalkan saat permintaan masih terbuka: catat sebagai belum setuju.
      const r = requestedRef.current;
      if (r) { try { r.onDecline(); } catch (_e) { /* abaikan */ } }
    };
  }, []);

  const finishRequest = (agreed) => {
    const r = requestedRef.current;
    if (!r) return;
    requestedRef.current = null;
    setRequested(null);
    try { (agreed ? r.onAgree : r.onDecline)(); } catch (_e) { /* abaikan */ }
  };

  const closeDialog = () => {
    if (saving) return; // sedang menyimpan persetujuan: jangan ditutup lewat Escape atau klik luar
    setOpen(false);
    finishRequest(false);
  };

  const handleAgree = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/nakes/location-consent`, { agree: true }, { headers: authHeaders(), timeout: 15000 });
      setNeedsPrompt(false);
      setOpen(false);
      // Kalau jendela dibuka dari tombol OTW: lanjutkan hitung estimasi sekarang.
      finishRequest(true);
      // Pancing izin HP sekarang, supaya saat OTW tidak ada kejutan.
      try {
        navigator.geolocation?.getCurrentPosition(
          () => { setPermState('granted'); reportPermission('granted'); },
          (err) => { if (err?.code === 1) { setPermState('denied'); reportPermission('denied'); } },
          { timeout: 10000, maximumAge: 60000 },
        );
      } catch (_e) { /* abaikan */ }
    } catch (_e) {
      /* gagal simpan (sinyal): dialog tetap terbuka, nakes bisa coba lagi */
    } finally {
      setSaving(false);
    }
  };

  // Tombol "Izinkan lokasi" di banner (status belum diizinkan): memunculkan pertanyaan izin dari HP.
  const askLocation = () => { recheckPermission(); };

  // Layar kerja (OTW, tindakan, konfirmasi layanan, upload foto/IC, pemakaian stok): jangan diganggu.
  const isWorkScreen = pathname.includes('/workflow') || /\/jobs\/[^/]+\/[^/]+/.test(pathname);

  // Kartu pengingat hanya untuk status ditolak / belum diizinkan. Status "tidak terbaca" tidak diganggu.
  // Tidak muncul di layar kerja (workflow job) dan tidak bertumpuk dengan pop up persetujuan.
  // Berlaku untuk semua nakes, termasuk yang belum setuju persetujuan lokasi. Menunggu cek persetujuan
  // selesai supaya tidak berebut dengan pop up persetujuan, dan mengalah selama pop up itu terbuka.
  const needsReminder = LOCATION_REMINDER_ENABLED && (permState === 'denied' || permState === 'prompt');
  const nagEligible = consentChecked && needsReminder && !open && !requested && !isWorkScreen;

  useEffect(() => {
    if (!nagEligible) {
      if (nagOpen) { lastNagRef.current = Date.now(); setNagOpen(false); }
      return undefined;
    }
    if (nagOpen) return undefined;
    const check = () => {
      const gap = Date.now() - lastNagRef.current;
      if (gap >= NAG_EVERY_MS || gap < 0) setNagOpen(true); // gap negatif = jam HP dimundurkan
    };
    check();
    const t = setInterval(check, 15000);
    return () => clearInterval(t);
  }, [nagEligible, nagOpen]);

  // Selama pengingat tampil, baca ulang status izin tiap 15 detik, supaya pop up dan banner hilang
  // sendiri begitu izin diaktifkan walau aplikasi tidak pernah ditinggalkan (event perubahan izin tidak selalu datang).
  useEffect(() => {
    if (!needsReminder) return undefined;
    let alive = true;
    const t = setInterval(async () => {
      try {
        if (!navigator.permissions?.query) return;
        const st = await navigator.permissions.query({ name: 'geolocation' });
        if (alive) { setPermState(st.state); reportPermission(st.state); }
      } catch (_e) { /* abaikan */ }
    }, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [needsReminder, reportPermission]);

  const closeNag = () => { lastNagRef.current = Date.now(); setNagOpen(false); };

  return (
    <>
      {needsReminder && (
        <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-xs px-3 py-2" data-testid="location-reminder-banner">
          <div>
            {denied
              ? 'Lokasi belum aktif di HP kamu. Aktifkan izin lokasi untuk aplikasi ini supaya estimasi tiba bisa dihitung saat OTW.'
              : 'Izin lokasi belum diberikan untuk aplikasi ini. Izinkan supaya estimasi tiba bisa dihitung saat OTW.'}
          </div>
          <div className="mt-1.5 flex gap-2">
            {!denied && (
              <button type="button" onClick={askLocation} className="px-2 py-1 rounded bg-amber-600 text-white" data-testid="location-allow-button">
                Izinkan lokasi
              </button>
            )}
            <button type="button" onClick={() => setShowGuide((v) => !v)} className="px-2 py-1 rounded border border-amber-400" data-testid="location-guide-toggle">
              {showGuide ? 'Tutup panduan' : 'Cara mengaktifkan'}
            </button>
          </div>
          {showGuide && (
            <div className="mt-2 space-y-1.5" data-testid="location-guide">
              <LocationGuide />
            </div>
          )}
        </div>
      )}
      {nagEligible && nagOpen && (
        <LocationNagCard denied={denied} onRecheck={recheckPermission} onClose={closeNag} />
      )}
      {(needsPrompt || requested) && (
        <Dialog open={open} onOpenChange={(v) => { if (!v) closeDialog(); }}>
          <DialogContent className="max-w-md" data-testid="location-consent-popup">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5" /> Bantu kami memperkirakan waktu tibamu
              </DialogTitle>
              <DialogDescription>
                Saat kamu menekan OTW, aplikasi membaca lokasimu satu kali untuk menghitung perkiraan lama perjalanan
                ke lokasi customer. Setelah itu lokasimu tidak dilacak lagi. Estimasi ini membantu tim memantau
                dan menghubungi kamu bila perjalanan tampak tertahan.
                {requested && ' OTW kamu sudah tercatat. Setuju sekarang supaya estimasi tiba langsung dihitung.'}
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={closeDialog} disabled={saving}>Nanti</Button>
              <Button onClick={handleAgree} disabled={saving} data-testid="location-consent-agree">
                {saving ? 'Menyimpan...' : 'Setuju'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
